import React, { useCallback, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import api from "services/api"
import SimpleHeader from "components/Headers/SimpleHeader.js"
import Loading from "components/Modals/Loading"
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Modal,
  Row,
  Table,
} from "reactstrap"

const STATUS_LABEL = {
  pendente: "Aguardando",
  processando: "Processando",
  concluido: "Concluído",
  erro: "Erro",
}

const STATUS_COLOR = {
  pendente: "warning",
  processando: "info",
  concluido: "success",
  erro: "danger",
}

const EMPTY_NEW_DOCUMENT = { titulo: "", moduloSap: "", clienteId: "" }
const EMPTY_EDIT_FORM = { _id: null, titulo: "", moduloSap: "", clienteId: "" }
const GLOBAL_OPTION_LABEL = "— Global (todos os clientes) —"

export default function KnowledgeDocuments() {
  const [documents, setDocuments] = useState([])
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [newDocument, setNewDocument] = useState(EMPTY_NEW_DOCUMENT)
  const [newFile, setNewFile] = useState(null)
  const [addError, setAddError] = useState("")
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [editForm, setEditForm] = useState(EMPTY_EDIT_FORM)
  const [editError, setEditError] = useState("")
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [deleteNotice, setDeleteNotice] = useState(null)
  const [documentToDelete, setDocumentToDelete] = useState(null)
  const fileInputRef = useRef(null)
  const pollRef = useRef(null)

  const clienteNomeById = customers.reduce((acc, customer) => {
    acc[customer._id] = customer.shortName || customer.fullName
    return acc
  }, {})

  const clienteNome = doc => (doc.clienteId ? clienteNomeById[doc.clienteId] || "Cliente removido" : "Global")

  const fetchCustomers = useCallback(async () => {
    const response = await api.get("customer/all")
    setCustomers(response.data)
  }, [])

  const fetchDocuments = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true)
    try {
      const response = await api.get("knowledgeSource/all?sourceType=document")
      setDocuments(response.data)
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchDocuments()
    fetchCustomers()
  }, [fetchDocuments, fetchCustomers])

  useEffect(() => {
    const hasActiveJob = documents.some(
      doc => doc.status === "pendente" || doc.status === "processando"
    )

    if (hasActiveJob && !pollRef.current) {
      pollRef.current = setInterval(() => fetchDocuments(false), 8000)
    }

    if (!hasActiveJob && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current)
        pollRef.current = null
      }
    }
  }, [documents, fetchDocuments])

  const handleAddDocument = async e => {
    e.preventDefault()

    if (!newFile) {
      setAddError("Selecione um arquivo (PDF, DOCX ou PPTX).")
      return
    }

    setSaving(true)
    setAddError("")

    try {
      const formData = new FormData()
      formData.append("file", newFile, newFile.name)
      formData.append("titulo", newDocument.titulo)
      formData.append("moduloSap", newDocument.moduloSap)
      formData.append("clienteId", newDocument.clienteId)

      await api.post("knowledgeSource/create/document", formData)
      setNewDocument(EMPTY_NEW_DOCUMENT)
      setNewFile(null)
      if (fileInputRef.current) fileInputRef.current.value = ""
      await fetchDocuments()
    } catch (err) {
      setAddError(err?.response?.data?.error || "Erro ao adicionar o documento.")
    } finally {
      setSaving(false)
    }
  }

  const openEditModal = doc => {
    setEditForm({
      _id: doc._id,
      titulo: doc.titulo,
      moduloSap: doc.moduloSap || "",
      clienteId: doc.clienteId || "",
    })
    setEditError("")
    setEditModalOpen(true)
  }

  const closeEditModal = () => {
    if (saving) return
    setEditModalOpen(false)
  }

  const handleEditSave = async e => {
    e.preventDefault()
    setSaving(true)
    setEditError("")

    try {
      await api.put(`knowledgeSource/update/${editForm._id}`, {
        titulo: editForm.titulo,
        moduloSap: editForm.moduloSap,
        clienteId: editForm.clienteId,
      })
      setEditModalOpen(false)
      await fetchDocuments()
    } catch (err) {
      setEditError(err?.response?.data?.error || "Erro ao salvar o documento.")
    } finally {
      setSaving(false)
    }
  }

  const openDeleteModal = doc => {
    setDocumentToDelete(doc)
    setDeleteModalOpen(true)
  }

  const confirmDelete = async () => {
    setSaving(true)
    try {
      const deletedTitle = documentToDelete.titulo
      await api.delete(`knowledgeSource/delete/${documentToDelete._id}`)
      setDeleteModalOpen(false)
      setDocumentToDelete(null)
      //====== WR data 06/10/2026 — pedido do usuário: depois de excluir, o índice de busca do
      // Discovery Engine ainda pode servir o documento por alguns minutos (a exclusão em si já
      // foi concluída no servidor) — avisa pra ninguém tomar a citação residual por falha.
      setDeleteNotice(
        `Documento "${deletedTitle}" excluído. Pode levar alguns minutos até ele deixar de aparecer nas respostas do chat.`
      )
      await fetchDocuments()
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <SimpleHeader
        name="Documentos"
        items={[{ level: "nav", name: "Base de Conhecimento do SmartChat" }]}
      />
      <Container className="mt--6" fluid>
        <Row>
          <Col xl="12">
            <Link to="/admin/knowledge-base" className="text-muted small">
              <i className="ni ni-bold-left" /> Voltar pra Base de Conhecimento
            </Link>
            {deleteNotice && (
              <Alert color="success" className="mt-3" toggle={() => setDeleteNotice(null)}>
                {deleteNotice}
              </Alert>
            )}
          </Col>
        </Row>
        <Row className="mt-3">
          <Col xl="12">
            <Card>
              <CardHeader>
                <h5 className="h3 mb-0">Adicionar documento</h5>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleAddDocument}>
                  {addError && <div className="text-danger mb-3">{addError}</div>}
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <label className="form-control-label">Arquivo (PDF, DOCX ou PPTX)</label>
                        <Input
                          innerRef={fileInputRef}
                          required
                          type="file"
                          accept=".pdf,.docx,.pptx"
                          disabled={saving}
                          onChange={e => setNewFile(e.target.files?.[0] || null)}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <label className="form-control-label">Título</label>
                        <Input
                          required
                          type="text"
                          placeholder="Título do documento"
                          value={newDocument.titulo}
                          disabled={saving}
                          onChange={e => setNewDocument({ ...newDocument, titulo: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="3">
                      <FormGroup>
                        <label className="form-control-label">Módulo SAP</label>
                        <Input
                          type="text"
                          placeholder="Ex: Financeiro, Compras..."
                          value={newDocument.moduloSap}
                          disabled={saving}
                          onChange={e => setNewDocument({ ...newDocument, moduloSap: e.target.value })}
                        />
                      </FormGroup>
                    </Col>
                    <Col md="2" className="d-flex align-items-end">
                      <FormGroup className="w-100">
                        <Button
                          color="primary"
                          type="submit"
                          disabled={saving}
                          block
                          style={{ whiteSpace: "nowrap" }}
                        >
                          {saving ? "Adicionando..." : "Adicionar"}
                        </Button>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="4">
                      <FormGroup>
                        <label className="form-control-label">Cliente</label>
                        <Input
                          type="select"
                          value={newDocument.clienteId}
                          disabled={saving}
                          onChange={e => setNewDocument({ ...newDocument, clienteId: e.target.value })}
                        >
                          <option value="">{GLOBAL_OPTION_LABEL}</option>
                          {customers.map(customer => (
                            <option key={customer._id} value={customer._id}>
                              {customer.shortName || customer.fullName}
                            </option>
                          ))}
                        </Input>
                        <div className="text-muted small mt-1">
                          Deixe em "Global" pra conteúdo genérico (visível pra
                          todo mundo). Escolha um cliente pra restringir esse
                          documento só a ele.
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col xl="12">
            <Card>
              <CardHeader>
                <Row>
                  <Col xs="6">
                    <h5 className="h3 mb-0">Documentos da base de conhecimento</h5>
                  </Col>
                  <Col xs="6" style={{ textAlignLast: "end" }}>
                    <span className="text-muted">{documents.length} documento(s)</span>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody>
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Título</th>
                      <th>Arquivo</th>
                      <th>Módulo SAP</th>
                      <th>Cliente</th>
                      <th>Status</th>
                      <th>Chunks</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.map(doc => (
                      <tr key={doc._id}>
                        <td>{doc.titulo}</td>
                        <td>
                          {doc.originalFileName || "-"}
                          {doc.fileExtension && (
                            <Badge color="secondary" className="ml-2">
                              {doc.fileExtension.toUpperCase()}
                            </Badge>
                          )}
                        </td>
                        <td>{doc.moduloSap || "-"}</td>
                        <td>
                          <Badge color={doc.clienteId ? "info" : "secondary"}>
                            {clienteNome(doc)}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={STATUS_COLOR[doc.status] || "secondary"}>
                            {STATUS_LABEL[doc.status] || doc.status}
                          </Badge>
                          {doc.status === "erro" && doc.errorMessage && (
                            <div className="text-muted small mt-1">
                              {doc.errorMessage}
                            </div>
                          )}
                        </td>
                        <td>{doc.chunkCount || 0}</td>
                        <td>
                          <Button
                            color="secondary"
                            size="sm"
                            disabled={doc.status === "processando"}
                            onClick={() => openEditModal(doc)}
                          >
                            Editar
                          </Button>{" "}
                          <Button
                            color="danger"
                            size="sm"
                            disabled={doc.status === "processando"}
                            onClick={() => openDeleteModal(doc)}
                          >
                            Excluir
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {documents.length === 0 && !loading && (
                      <tr>
                        <td colSpan="7" className="text-center text-muted">
                          Nenhum documento cadastrado ainda.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal className="modal-dialog-centered" isOpen={editModalOpen} autoFocus={false}>
        <div className="modal-header">
          <h5 className="modal-title">Editar documento</h5>
          <button
            aria-label="Close"
            className="close"
            type="button"
            onClick={closeEditModal}
            disabled={saving}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <Form onSubmit={handleEditSave}>
          <div className="modal-body">
            {editError && <div className="text-danger mb-3">{editError}</div>}
            <FormGroup>
              <label className="form-control-label">Título</label>
              <Input
                required
                type="text"
                placeholder="Título do documento"
                value={editForm.titulo}
                disabled={saving}
                onChange={e => setEditForm({ ...editForm, titulo: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <label className="form-control-label">Módulo SAP</label>
              <Input
                type="text"
                placeholder="Ex: Financeiro, Compras, Vendas..."
                value={editForm.moduloSap}
                disabled={saving}
                onChange={e => setEditForm({ ...editForm, moduloSap: e.target.value })}
              />
            </FormGroup>
            <FormGroup>
              <label className="form-control-label">Cliente</label>
              <Input
                type="select"
                value={editForm.clienteId}
                disabled={saving}
                onChange={e => setEditForm({ ...editForm, clienteId: e.target.value })}
              >
                <option value="">{GLOBAL_OPTION_LABEL}</option>
                {customers.map(customer => (
                  <option key={customer._id} value={customer._id}>
                    {customer.shortName || customer.fullName}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <div className="text-muted small">
              Alterar o título ou o cliente reprocessa o documento do zero
              (apaga os trechos antigos e reingere o conteúdo a partir do
              arquivo já enviado, sem precisar enviá-lo de novo). Alterar só
              o módulo SAP não reprocessa. Para trocar o arquivo, exclua e
              cadastre de novo.
            </div>
          </div>
          <div className="modal-footer">
            <Button color="link" type="button" onClick={closeEditModal} disabled={saving}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </div>
        </Form>
      </Modal>

      <Modal
        className="modal-dialog-centered"
        isOpen={deleteModalOpen}
        autoFocus={false}
      >
        <div className="modal-header">
          <h5 className="modal-title">Excluir documento</h5>
          <button
            aria-label="Close"
            className="close"
            type="button"
            onClick={() => setDeleteModalOpen(false)}
            disabled={saving}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          Tem certeza que deseja excluir o documento{" "}
          <strong>{documentToDelete?.titulo}</strong> da base de conhecimento?
          Essa ação remove o conteúdo do SmartChat e o arquivo original e não
          pode ser desfeita. O chat pode continuar citando o documento por
          alguns minutos, até o índice de busca atualizar.
        </div>
        <div className="modal-footer">
          <Button
            color="link"
            type="button"
            onClick={() => setDeleteModalOpen(false)}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button color="danger" type="button" onClick={confirmDelete} disabled={saving}>
            {saving ? "Excluindo..." : "Excluir"}
          </Button>
        </div>
      </Modal>

      <Loading modalOpened={loading} />
    </>
  )
}
